/**
 * VideoProcessor — inert design-clone implementation.
 *
 * Upstream this ran MediaPipe Selfie Segmentation over the local camera feed
 * to composite a virtual background or a soft-focus blur, then handed the
 * resulting canvas track to `webRTCManager.replaceVideoTrack()` so peers
 * received the processed image.
 *
 * This clone has no camera and no peers, and `@mediapipe/selfie_segmentation`
 * is no longer a dependency. What remains is everything the UI reads:
 * `VideoEffectMode` and `DEFAULT_BACKGROUNDS` still drive CallLayout's
 * background picker, so the picker, its thumbnails, the intensity slider, and
 * the selected-state styling all render and persist exactly as before. The
 * compositor itself is a no-op.
 *
 * The background images under `public/backgrounds/` are still shipped — they
 * are what the picker displays.
 */

export type VideoEffectMode = 'none' | 'blur' | 'picture';

/** Default background picker entries — keep in lock-step with CallLayout's UI. */
export const DEFAULT_BACKGROUNDS: ReadonlyArray<{ id: string; src: string; label: string }> = [
  { id: 'ps95_neon_a',     src: '/backgrounds/ps95_neon_a.png',     label: 'PSYDECHAT95' },
  { id: 'ps95_neon_b',     src: '/backgrounds/ps95_neon_b.jpg',     label: 'PSYDECHAT95 · Neon' },
  { id: 'ps95_uwu',        src: '/backgrounds/ps95_uwu.png',        label: 'PSYDECHAT95 · UwU' },
  { id: 'pixel_quest',     src: '/backgrounds/pixel_quest.png',     label: 'Pixel Quest' },
];

export interface VideoProcessorOptions {
  sourceStream: MediaStream;
  mode: VideoEffectMode;
  bgSrc?: string | null;
  /** 0-20, blur radius in px for the Soft Focus mode. Clamped on apply. */
  intensity?: number;
  /**
   * Upstream: fired once the compositor produced its first usable frame, so
   * the caller could swap peers over to the processed track. Never fires
   * here — there is no compositor and no peer to swap.
   */
  onFirstCompositeReady?: (track: MediaStreamTrack) => void;
  /** Called on every compositor error so callers can surface UI banners. */
  onError?: (message: string) => void;
}

export class VideoProcessor {
  private _mode: VideoEffectMode;
  private _bgSrc: string | null;
  private _intensity: number;

  constructor(opts: VideoProcessorOptions) {
    this._mode = opts.mode;
    this._bgSrc = opts.bgSrc ?? null;
    this._intensity = this._clampIntensity(opts.intensity ?? 8);
  }

  private _clampIntensity(n: number): number {
    return Math.min(20, Math.max(0, Number.isFinite(n) ? n : 8));
  }

  public getMode(): VideoEffectMode { return this._mode; }
  public getBgSrc(): string | null { return this._bgSrc; }
  public getIntensity(): number { return this._intensity; }

  /** No compositor runs, so no error can be raised. */
  public getLastError(): string | null { return null; }

  /** No composite track is produced. Callers keep the source stream. */
  public getCompositeStream(): MediaStream | null { return null; }

  public setMode(mode: VideoEffectMode, bgSrc?: string | null): void {
    this._mode = mode;
    if (bgSrc !== undefined) this._bgSrc = bgSrc;
  }

  public setIntensity(n: number): void {
    this._intensity = this._clampIntensity(n);
  }

  public async start(): Promise<void> {}

  public stop(): void {}

  public releaseHeavyResources(): void {}

  /**
   * Preloads a background image. Retained because it is genuinely useful for
   * the picker: it warms the browser cache so thumbnails do not pop in.
   */
  public async ensureBackgroundLoaded(src: string): Promise<HTMLImageElement | null> {
    if (typeof document === 'undefined') return null;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }
}

/**
 * Hook-style helper for parity with `audioProcessing.ts#createAudioProcessor`.
 */
export function createVideoProcessor(opts: VideoProcessorOptions): VideoProcessor {
  return new VideoProcessor(opts);
}
